import React from 'react';
import {
  BRANCH_STEM,
  C,
  CIRCLE_R,
  PLUS_R,
  VIDEO_CARD_GAP,
  VIDEO_CARD_H,
  VIDEO_CARD_W,
  halfW,
  normalizeVideoField,
  videoUrl,
} from '../flowCanvas.constants';

const PAD = 10;
const HEADER_H = 28;
const PREVIEW_H = 96;
const META_Y = HEADER_H + PREVIEW_H + 18;

export default function VideoStackNode({
  step,
  stepX,
  stepCY,
  parentStep,
  parentStepX,
  parentStepY,
  nextStep,
  nextStepX,
  nextStepY,
  nextStepHalfW,
  items,
  activeStepId,
  activeGraphicIndex,
  assetFolder,
  campaignId,
  onVideoClick,
  onAddItemClick,
  onInfoClick,
  onRemoveVideo,
}) {
  const videos = Array.isArray(items) ? items.filter(Boolean) : [];
  const rows = videos.length > 0 ? videos : [null];
  const count = rows.length;
  const stopBubble = (e) => {
    e.stopPropagation();
    if (typeof e.preventDefault === 'function' && (e.type === 'pointerdown' || e.type === 'mousedown')) {
      // Prevent the parent SVG/canvas from treating native video control presses as node interaction.
      e.preventDefault();
    }
  };

  const setMidpointPreviewFrame = (e) => {
    const videoElement = e.currentTarget;
    if (!videoElement || videoElement.dataset.midpointPreviewApplied === 'true') {
      return;
    }

    const duration = Number(videoElement.duration);
    if (!Number.isFinite(duration) || duration <= 0) {
      return;
    }

    // Use a middle frame so transition videos that start transparent still have a useful thumbnail.
    const midpoint = Math.min(Math.max(duration / 2, 0.1), Math.max(duration - 0.1, 0.1));
    videoElement.dataset.midpointPreviewApplied = 'true';

    const seekToMidpoint = () => {
      videoElement.currentTime = midpoint;
      videoElement.removeEventListener('canplay', seekToMidpoint);
    };

    if (videoElement.readyState >= 1) {
      seekToMidpoint();
    } else {
      videoElement.addEventListener('canplay', seekToMidpoint, { once: true });
    }
  };

  const parentHalfW = parentStep ? halfW(parentStep) : CIRCLE_R;
  const hasParent = parentStepX !== null && parentStepX !== undefined;
  const hasNext = nextStep && nextStepX !== null && nextStepX !== undefined && nextStepY !== null && nextStepY !== undefined;

  const totalH = count * (VIDEO_CARD_H + VIDEO_CARD_GAP) - VIDEO_CARD_GAP;
  const cardLeftX = stepX - VIDEO_CARD_W / 2;
  const stackTopY = stepCY - totalH / 2;

  const sourceX = hasParent ? parentStepX + parentHalfW : cardLeftX - BRANCH_STEM;
  const sourceY = parentStepY ?? stepCY;
  const branchMidX = Math.max(sourceX + BRANCH_STEM, (sourceX + cardLeftX) / 2);
  const infoX = sourceX + PLUS_R + 14;
  const addX = infoX + PLUS_R + 18 + PLUS_R;
  const firstCardY = stackTopY + VIDEO_CARD_H / 2;
  const lastCardY = stackTopY + (count - 1) * (VIDEO_CARD_H + VIDEO_CARD_GAP) + VIDEO_CARD_H / 2;

  const rightEdgeX = stepX + VIDEO_CARD_W / 2;
  const exitSpineX = rightEdgeX + 34;
  const nextAnchorX = hasNext ? nextStepX - (nextStepHalfW || 0) : null;
  const branchColor = videos.length > 0 ? C.purple : C.orange;

  return (
    <g className="fc-node fc-node--video-stack">
      {hasParent && (
        <g style={{ pointerEvents: 'none' }}>
          <line x1={sourceX} y1={sourceY} x2={infoX - PLUS_R} y2={sourceY} stroke={branchColor} strokeWidth="1.5" strokeOpacity="0.78" strokeLinecap="square" />
          <line x1={infoX + PLUS_R} y1={sourceY} x2={addX - PLUS_R} y2={sourceY} stroke={branchColor} strokeWidth="1.5" strokeOpacity="0.78" strokeLinecap="square" />
          <line x1={addX + PLUS_R} y1={sourceY} x2={branchMidX} y2={sourceY} stroke={branchColor} strokeWidth="1.5" strokeOpacity="0.78" strokeLinecap="square" />
          {Math.abs(sourceY - stepCY) > 2 && (
            <line x1={branchMidX} y1={sourceY} x2={branchMidX} y2={stepCY} stroke={branchColor} strokeWidth="1.5" strokeOpacity="0.78" strokeLinecap="square" />
          )}
          {(count > 1 || Math.abs(stepCY - firstCardY) > 2 || Math.abs(stepCY - lastCardY) > 2) && (
            <line x1={branchMidX} y1={Math.min(firstCardY, stepCY)} x2={branchMidX} y2={Math.max(lastCardY, stepCY)} stroke={branchColor} strokeWidth="1.5" strokeOpacity="0.5" />
          )}
          {rows.map((_, index) => {
            const cy = stackTopY + index * (VIDEO_CARD_H + VIDEO_CARD_GAP) + VIDEO_CARD_H / 2;
            return (
              <React.Fragment key={`video-branch-${index}`}>
                <line x1={branchMidX} y1={cy} x2={cardLeftX} y2={cy} stroke={branchColor} strokeWidth="1.5" strokeOpacity="0.78" strokeLinecap="square" />
                <circle cx={cardLeftX} cy={cy} r="3" fill={branchColor} fillOpacity="0.9" />
              </React.Fragment>
            );
          })}
          <text x={addX} y={sourceY - PLUS_R - 8} textAnchor="middle" fontSize="9.5" fontWeight="600" fill={C.greenText} style={{ userSelect: 'none' }}>
            Add Video
          </text>
        </g>
      )}

      {hasParent && (
        <g onClick={(e) => { e.stopPropagation(); onInfoClick?.(); }} style={{ cursor: 'pointer' }}>
          <circle cx={infoX} cy={sourceY} r={PLUS_R} fill={C.neonBlue} />
          <text x={infoX} y={sourceY + 1} textAnchor="middle" dominantBaseline="middle" fontSize="12" fontWeight="700" fill="#fff" style={{ pointerEvents: 'none', userSelect: 'none' }}>
            i
          </text>
        </g>
      )}

      {hasParent && (
        <g onClick={(e) => { e.stopPropagation(); onAddItemClick?.(); }} style={{ cursor: 'pointer' }}>
          <circle cx={addX} cy={sourceY} r={PLUS_R} fill={C.greenText} />
          <text x={addX} y={sourceY + 1} textAnchor="middle" dominantBaseline="middle" fontSize="16" fontWeight="300" fill="#fff" style={{ pointerEvents: 'none', userSelect: 'none' }}>
            +
          </text>
        </g>
      )}

      {rows.map((item, index) => {
        const cy = stackTopY + index * (VIDEO_CARD_H + VIDEO_CARD_GAP) + VIDEO_CARD_H / 2;
        const top = cy - VIDEO_CARD_H / 2;
        const isSelected = activeStepId === step.stepId && activeGraphicIndex === index;
        const videoField = normalizeVideoField(item?.filename);
        const videoSrc = videoField.srcUrl || videoUrl(videoField.name, videoField.isDefault, assetFolder, campaignId, videoField.type);
        const title = item?.name?.trim?.() || `Video ${index + 1}`;
        const filename = videoField.name || 'No file selected';

        return (
          <g key={`${step.stepId}-${index}`}>
            <rect x={cardLeftX + 2} y={top + 4} width={VIDEO_CARD_W} height={VIDEO_CARD_H} rx="8" fill="#000" fillOpacity="0.3" style={{ pointerEvents: 'none' }} />
            <rect
              x={cardLeftX}
              y={top}
              width={VIDEO_CARD_W}
              height={VIDEO_CARD_H}
              rx="8"
              fill={isSelected ? C.cardSel : C.card}
              stroke={isSelected ? C.sel : branchColor}
              strokeWidth={isSelected ? 2 : 1.5}
              style={{ cursor: 'pointer' }}
              onClick={(e) => { e.stopPropagation(); onVideoClick?.(index); }}
            />

            <line
              x1={cardLeftX + 1}
              y1={top + HEADER_H}
              x2={cardLeftX + VIDEO_CARD_W - 1}
              y2={top + HEADER_H}
              stroke={C.border}
              strokeWidth="1"
              style={{ pointerEvents: 'none' }}
            />

            <text x={cardLeftX + PAD} y={top + 19} fontSize="11" fontWeight="700" fill={C.text} style={{ pointerEvents: 'none', userSelect: 'none' }}>
              {title}
            </text>

            {/* Trash button — only shown when there are multiple videos */}
            {onRemoveVideo && videos.length > 1 && (
              <g onClick={e => { e.stopPropagation(); onRemoveVideo(index); }} style={{ cursor: 'pointer' }}>
                <circle cx={cardLeftX + VIDEO_CARD_W - PAD - 2} cy={top + HEADER_H / 2} r={9} fill="#dc2626" />
                <g transform={`translate(${cardLeftX + VIDEO_CARD_W - PAD - 2}, ${top + HEADER_H / 2})`}
                  style={{ pointerEvents: 'none', userSelect: 'none' }}>
                  <rect x="-2" y="-5" width="4" height="2" rx="1" fill="#fee2e2" />
                  <rect x="-4.5" y="-3" width="9" height="1.5" rx="0.75" fill="#fee2e2" />
                  <rect x="-3.5" y="-1" width="7" height="5.5" rx="0.8" fill="#fee2e2" />
                  <rect x="-1.8" y="0.5" width="1" height="3" rx="0.5" fill="#dc2626" />
                  <rect x="0.8" y="0.5" width="1" height="3" rx="0.5" fill="#dc2626" />
                </g>
              </g>
            )}

            {videoSrc ? (
              <foreignObject
                x={cardLeftX + PAD}
                y={top + HEADER_H + 8}
                width={VIDEO_CARD_W - PAD * 2}
                height={PREVIEW_H}
                onClick={stopBubble}
                onMouseDown={stopBubble}
                onPointerDown={stopBubble}
              >
                <div
                  xmlns="http://www.w3.org/1999/xhtml"
                  style={{ width: '100%', height: '100%', position: 'relative' }}
                  onClick={stopBubble}
                  onMouseDown={stopBubble}
                  onPointerDown={stopBubble}
                >
                  <video
                    src={videoSrc}
                    controls
                    preload="metadata"
                    playsInline
                    onLoadedMetadata={setMidpointPreviewFrame}
                    onClick={stopBubble}
                    onMouseDown={stopBubble}
                    onPointerDown={stopBubble}
                    style={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '6px',
                      background: '#060914',
                      objectFit: 'cover',
                    }}
                  />
                </div>
              </foreignObject>
            ) : (
              <g>
                <rect
                  x={cardLeftX + PAD}
                  y={top + HEADER_H + 8}
                  width={VIDEO_CARD_W - PAD * 2}
                  height={PREVIEW_H}
                  rx="6"
                  fill="rgba(255,255,255,0.04)"
                  stroke={C.border}
                  strokeDasharray="5 3"
                />
                <text x={stepX} y={top + HEADER_H + 48} textAnchor="middle" fontSize="11" fontWeight="700" fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
                  No video selected
                </text>
              </g>
            )}

            <text x={cardLeftX + PAD} y={top + META_Y} fontSize="8.5" fontWeight="700" fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
              File
            </text>
            <text x={cardLeftX + PAD} y={top + META_Y + 14} fontSize="9.5" fill={C.text} style={{ pointerEvents: 'none', userSelect: 'none' }}>
              {filename.length > 28 ? `${filename.slice(0, 28)}...` : filename}
            </text>

            {!item && (
              <text x={stepX} y={top + VIDEO_CARD_H - 14} textAnchor="middle" fontSize="8.5" fill={C.textDim} style={{ pointerEvents: 'none', userSelect: 'none' }}>
                Click the add button to create your first video node
              </text>
            )}
          </g>
        );
      })}

      {hasNext && (
        <g style={{ pointerEvents: 'none' }}>
          {rows.map((_, index) => {
            const cy = stackTopY + index * (VIDEO_CARD_H + VIDEO_CARD_GAP) + VIDEO_CARD_H / 2;
            return (
              <React.Fragment key={`video-exit-${index}`}>
                <line x1={rightEdgeX} y1={cy} x2={exitSpineX} y2={cy} stroke={branchColor} strokeWidth="1.5" strokeOpacity="0.78" strokeLinecap="square" />
                <circle cx={rightEdgeX} cy={cy} r="3" fill={branchColor} fillOpacity="0.9" />
              </React.Fragment>
            );
          })}
          {(count > 1 || Math.abs(stepCY - nextStepY) > 2) && (
            <line x1={exitSpineX} y1={Math.min(firstCardY, nextStepY)} x2={exitSpineX} y2={Math.max(lastCardY, nextStepY)} stroke={branchColor} strokeWidth="1.5" strokeOpacity="0.5" />
          )}
          <line x1={exitSpineX} y1={nextStepY} x2={nextAnchorX} y2={nextStepY} stroke={branchColor} strokeWidth="1.5" strokeOpacity="0.78" strokeLinecap="square" />
        </g>
      )}
    </g>
  );
}